import { ParsedUrlQuery } from 'querystring';

import { SubscriptionApiResponse } from 'src/services/responseTypes/subscriptionApiResponse';

import {
  PromoResponseItem,
  ProductDetail
} from '../../services/responseTypes/applyPromoResponse';
import {
  Data,
  PaymentProvider,
  Plan
} from '../../services/responseTypes/displayPlansResponse';
import { PlansMetaData } from '../../services/responseTypes/plansMetaDataResponse';
import {
  deleteLocalStorage,
  getCookie,
  getLocalStorage,
  setCookie,
  setLocalStorage
} from '../browserStorageHandler';
import { parseJwt } from '../common';
import { ActivePromotions, DynamicObject } from '../commonTypes';
import { REFERRAL_DISCOUNT_EXPIRY, REFERRER } from '../constants/AppConstants';
import { PAYMENT_PROVIDER } from '../constants/cookieConstants';
import {
  CONSUMPTION_JOURNEY_CODE_REDEMPTION_OBJ,
  CONSUMPTION_PAYMENT_JOURNEY_PATH,
  IS_CONSUMPTION_PAYMENT_JOURNEY,
  SUBSCRIPTION_ORDER_SUMMARY_BANNER,
  REFERRAL_CODE,
  REFERRAL_ID,
  REFERRAL_NAME,
  SELECTED_PLAN,
  PAYMENT_OBJECT,
  REFERRAL_USERID,
  USER_TOKEN,
  PAYMENT_JOURNEY_SOURCE
} from '../constants/localStorageConstants';
import { DOLLAR_SYMBOL, RUPEE_SYMBOL } from '../constants/stringConstants';
import {
  GLOBAL_PAYMENT_PROVIDERS,
  GLOBAL_PAYMENT_PROVIDERS_INTERFACE
} from '../constants/utilConstants';
import { getImagesDimentionByResolution } from '../ImageHelper/ImagesDimensionHelper';
import { subscriptionDataType } from '../../store/interface';
import { TvShowRelatedContent } from '../../services/GraphQL/Queries/types';

export const calculateSellPrice = (
  plan: Plan,
  activePromos: ActivePromotions,
  consumptionBuyPlan?: boolean
) => {
  if (activePromos[plan.id]) {
    if (consumptionBuyPlan) {
      let promoDiscount = 0;
      const { discount, discount_type: discountType = '' } =
        activePromos[plan.id].promotions[0];
      if (discountType.toLowerCase() === 'currency') {
        promoDiscount = Number(discount) ? discount : 0;
      } else if (discountType.toLowerCase() === 'percentage') {
        promoDiscount = (discount * activePromos[plan.id].price) / 100;
      }
      return (activePromos[plan.id].price - promoDiscount).toFixed(2);
    } else {
      return activePromos[plan.id]?.actualPrice;
    }
  }
  if (plan?.discount?.code) {
    return plan.actual_price;
  }
  return plan.sell_price;
};
export const calculateDisplayPrice = (
  plan: Plan,
  activePromos: ActivePromotions,
  consumptionBuyPlan?: boolean
) => {
  if (activePromos[plan.id]) {
    if (consumptionBuyPlan) {
      return activePromos[plan.id].price;
    } else {
      return activePromos[plan.id].sellPrice;
    }
  }
  return plan.display_price;
};

export const calculateDiscountPercentage = (
  plan: Plan,
  activePromos: ActivePromotions,
  consumptionBuyPlan?: boolean
) => {
  return Math.floor(
    ((calculateDisplayPrice(plan, activePromos, consumptionBuyPlan) -
      Number(calculateSellPrice(plan, activePromos, consumptionBuyPlan))) *
      100) /
      calculateDisplayPrice(plan, activePromos, consumptionBuyPlan)
  );
};

export const getPromoDiscount = (promo: PromoResponseItem) => {
  let promoDiscount = 0;
  const { discount, discount_type: discountType = '' } = promo.promotions[0];
  if (discountType.toLowerCase() === 'currency') {
    promoDiscount = Number(discount) ? discount : 0;
  } else if (discountType.toLowerCase() === 'percentage') {
    promoDiscount = (discount * promo.price) / 100;
  }
  return Math.round((promoDiscount / promo.price) * 100) + '%';
};

/**
 * Method for calculating discount as per New Promotion API response
 */
export const getPromoDiscountNew = (promo: ProductDetail) => {
  const { discount, sellPrice } = promo;
  return Math.round((discount / sellPrice) * 100) + '%';
};

export const getCurrencySymbol = (currency: string) => {
  switch (currency) {
    case 'INR':
      return RUPEE_SYMBOL;
    case 'USD':
      return DOLLAR_SYMBOL;
    default:
      return currency;
  }
};

export const filterGlobalPlans = (plans: Plan[]) => {
  const paymentProvider = getCookie(PAYMENT_PROVIDER);
  if (!paymentProvider) {
    return plans;
  }
  const subscriptionPlans: Plan[] = plans.filter((plan: Plan) => {
    // If paymentprovider mentioned in URL, assigning that paymentProvider to show in FE
    return paymentFilter(plan.payment_providers);
  });
  return subscriptionPlans;
};

const paymentFilter = (paymentProviders: PaymentProvider[]) => {
  const paymentProvider = getCookie(PAYMENT_PROVIDER)
    ?.toString()
    .toLowerCase() as keyof GLOBAL_PAYMENT_PROVIDERS_INTERFACE;
  const globalPaymentProviders = Object.keys(GLOBAL_PAYMENT_PROVIDERS);
  return paymentProviders.some((provider: PaymentProvider) => {
    if (
      (globalPaymentProviders.includes(paymentProvider) &&
        GLOBAL_PAYMENT_PROVIDERS[paymentProvider].includes(
          provider.name.toLowerCase()
        )) ||
      provider.name.toLowerCase() === paymentProvider
    ) {
      return true;
    }
  });
};

// return url parameters as an object
export const getUrlParamsAsObject = (url: string) => {
  let queryStartIndex = 0;
  if (url && decodeURI(url).indexOf('?') > -1) {
    queryStartIndex = decodeURI(url).indexOf('?') + 1;
  }

  let queryParams = url.split('&');
  queryParams = queryParams.filter((item) => !!item);
  url = queryParams.join('&');

  const urlParams = [
    'tag=',
    't=',
    'utm_source=',
    'utm_medium=',
    'utm_campaign=',
    'utm_term=',
    'utm_content=',
    'dekey=',
    'sid=',
    'partner_platform=',
    'partner=',
    'plid=',
    'pp=',
    'ctma_id='
  ];

  if (
    (url && url.indexOf('code') > -1 && url.indexOf('autoapply') > -1) ||
    urlParams.some((param: string) => url && url.indexOf(param) > -1)
  ) {
    return JSON.parse(
      `{"${decodeURI(url)
        .substring(queryStartIndex)
        .replace(/"/g, '\\"')
        .replace(/&/g, '","')
        .replace(/=/g, '":"')}"}`
    );
  }
  return {};
};

export const getCurrentPlan = (
  plansData: Data | null,
  selectedPlanID: string
) => {
  return plansData?.plans?.find((plan) => plan.id === selectedPlanID) || null;
};

export const getSpecialPlan = (plans: Plan[] = [], defaultPlan: string) => {
  return (
    (plans.length && plans.find((plan) => plan.id === defaultPlan)) || null
  );
};

// function for creating the plan object that can be used by payment page
export const setPaymentData = (
  data: any,
  flags: { [key: string]: boolean },
  promo_code: string | null,
  country: string,
  isGlobalUpgrade: boolean
) => {
  const {
    BillDesk,
    PayTM,
    PayU,
    Mobikwik,
    Phonepe,
    Amazonpay_new: AmazonpayNew,
    Google,
    Qwikcilver,
    PaytmQR
  } = flags;
  const paymentProvider = getCookie(PAYMENT_PROVIDER)
    ?.toString()
    .toLowerCase() as keyof GLOBAL_PAYMENT_PROVIDERS_INTERFACE;
  let payProviders = data.paymentProviders || data.payment_providers; // Setting all payment providers available to that region by default
  // If paymentprovider mentioned in URL, assigning that paymentProvider to show in FE
  if (paymentProvider && country !== 'IN') {
    payProviders = [];
    const globalPaymentProviders = Object.keys(GLOBAL_PAYMENT_PROVIDERS);
    (data.paymentProviders || data.payment_providers).forEach(
      (item: PaymentProvider) => {
        if (
          (globalPaymentProviders.includes(paymentProvider) &&
            GLOBAL_PAYMENT_PROVIDERS[paymentProvider].includes(
              item.name.toLowerCase()
            )) ||
          item.name.toLowerCase() === paymentProvider
        ) {
          payProviders.push(item);
        }
      }
    );
    if (payProviders.length === 0)
      payProviders = data.paymentProviders || data.payment_providers;
  }
  return {
    pack: data.title || '',
    price:
      ((data?.discount?.code || country.toLowerCase() != 'in') &&
        data.actual_price) ||
      data.sellPrice ||
      data.sell_price ||
      data.price,
    currency: data.currency || '',
    pack_id: data.id || '',
    start_date: data.start || '',
    end_date: data.end || '',
    billing_frequency: data.billingFrequency || data.billing_frequency || '',
    country: country || data.country || '',
    device_number:
      data.numberOfSupportedDevices || data.number_of_supported_devices || -1,
    all_paymentProviders: payProviders,
    promotions: data.promotions,
    auto_renewal: data.recurring,
    tv_lang: data.tv_show_audio_languages,
    channel_lang: data.channel_audio_languages,
    movie_lang: data.movie_audio_languages,
    description: data.description || '',
    free_trial: data.freeTrial || data.free_trial || '',
    pack_type:
      data.subscriptionPlanType ||
      data.subscription_plan_type ||
      data.plan_type,
    promo_code,
    BillDesk,
    PayTM,
    PayU,
    Mobikwik,
    Phonepe,
    AmazonpayNew,
    Google,
    Qwikcilver,
    PaytmQR,
    billing_type: data.billingType || data.billing_type || '',
    allowed_playback_duration:
      data.allowedPlaybackDuration || data.allowed_playback_duration || '',
    offers: data.offers || [],
    tvodPackDetails: data.tvodPackDetails || {},
    duration_text:
      data.displayPrice ||
      data.display_price ||
      data.durationText ||
      data.duration_text ||
      '',
    original_price: data.original_price || data.actual_price || '',
    sell_price: data.sell_price,
    suggestionTag: data.suggession_tag,
    title: data.title,
    features: data.features,
    purchaseAllowed: data.purchase_allowed,
    priority: data.priority,
    isDefault: data.isDefault,
    duration: data.duration,
    payablePrice: data.sell_price,
    isGlobalUpgrade,
    sellPrice: data?.sell_price || data?.sellPrice
  };
};

export const buyPlan = (
  plansData: Data | null,
  plansMetaData: PlansMetaData[],
  currentPlan: Plan | null,
  planId: string,
  activePromos: ActivePromotions,
  promoCode: string,
  country: string,
  currentSubscription?: React.MutableRefObject<SubscriptionApiResponse | null> | null,
  consumptionBuyPlan?: boolean
) => {
  if (!plansData || !planId) return null;
  const selectedPlanId = planId;
  let promoDiscount;
  const selectedPlanMetadata = plansMetaData.find(
    (plan: PlansMetaData) => plan.id === selectedPlanId
  );
  const selectedPlanPayload: any = {
    ...(activePromos[selectedPlanId] || currentPlan),
    ...selectedPlanMetadata
  };

  // create dictionary of payment provider flags
  const paymentProviderFlags: { [key: string]: boolean } = {};

  const paymentProvider = getCookie(PAYMENT_PROVIDER)
    ?.toString()
    .toLowerCase() as keyof GLOBAL_PAYMENT_PROVIDERS_INTERFACE;
  if (country !== 'IN' && paymentProvider) {
    // If paymentprovider mentioned in URL, assigning that paymentProvider to show in FE
    const globalPaymentProviders = Object.keys(GLOBAL_PAYMENT_PROVIDERS);
    (
      selectedPlanPayload.paymentProviders ||
      selectedPlanPayload.payment_providers
    ).forEach((provider: PaymentProvider) => {
      if (
        (globalPaymentProviders.includes(paymentProvider) &&
          GLOBAL_PAYMENT_PROVIDERS[paymentProvider].includes(
            provider.name.toLowerCase()
          )) ||
        provider.name.toLowerCase() === paymentProvider
      ) {
        paymentProviderFlags[provider.name] = true;
      }
    });
  } else {
    const {
      all_paymentProviders = [],
      paymentProviders = [],
      payment_providers = []
    } = selectedPlanPayload;
    (paymentProviders || payment_providers || all_paymentProviders).forEach(
      (paymentProvider: PaymentProvider) => {
        paymentProviderFlags[paymentProvider.name] = true;
      }
    );
  }

  let promo_code = null;
  if (activePromos[selectedPlanId]) {
    promo_code = promoCode;
  }

  const isGlobalUpgrade: boolean =
    currentSubscription &&
    currentSubscription.current &&
    currentSubscription.current.id &&
    currentSubscription.current.subscription_plan &&
    country.toLowerCase() !== 'in'
      ? true
      : false;

  // format plans' data
  const selectedPlanData = setPaymentData(
    selectedPlanPayload,
    paymentProviderFlags,
    promo_code,
    country,
    isGlobalUpgrade
  );

  if (promoCode && currentPlan) {
    promoDiscount = {
      discount:
        currentPlan.sell_price -
        Number(
          calculateSellPrice(currentPlan, activePromos, consumptionBuyPlan)
        )
    };
  }

  if (currentPlan?.discount) {
    promoDiscount = { discount: currentPlan.discount };
  }

  setLocalStorage(
    SELECTED_PLAN,
    promoDiscount?.discount
      ? { ...selectedPlanData, ...promoDiscount }
      : selectedPlanData
  );
};

export const getBgDimension = (dimensionType: string, deviceWidth: number) => {
  const imageDimensionObj = getImagesDimentionByResolution(deviceWidth);
  if (dimensionType === 'height')
    return imageDimensionObj['portraitSmallBg'][1];
  return imageDimensionObj['portraitSmallBg'][0];
};

export const combineSimilarPlans = (plans: Plan[]) => {
  const uniquePlans: Plan[] = [];
  plans.forEach((plan: Plan) => {
    const uniquePlanIndex = uniquePlans.findIndex((uniqueplan: Plan) => {
      return uniqueplan.title === plan.title;
    });
    if (uniquePlanIndex > -1) {
      uniquePlans[uniquePlanIndex] =
        uniquePlans[uniquePlanIndex].sell_price > plan.sell_price
          ? uniquePlans[uniquePlanIndex]
          : plan;
    } else {
      uniquePlans.push(plan);
    }
  });
  return uniquePlans;
};

export const setGlobalPaymentProviders = (paramObject: ParsedUrlQuery) => {
  const globalPaymentProviders = Object.keys(GLOBAL_PAYMENT_PROVIDERS);
  if (
    paramObject.pp &&
    typeof paramObject.pp === 'string' &&
    (globalPaymentProviders.includes(paramObject.pp) ||
      GLOBAL_PAYMENT_PROVIDERS.simpaisa.includes(
        paramObject.pp.toLowerCase()
      ) ||
      GLOBAL_PAYMENT_PROVIDERS.cc.includes(paramObject.pp.toLowerCase()) ||
      GLOBAL_PAYMENT_PROVIDERS.mife.includes(paramObject.pp.toLowerCase()))
  ) {
    setCookie(PAYMENT_PROVIDER, paramObject.pp, 0, 30);
  }
};

// days to month or year converter
export const getDaysInYearOrMonth = (
  days: number,
  translationNode: DynamicObject,
  showYearInMonths = false
) => {
  if (Object.keys(translationNode).length) {
    const monthCount = Math.floor(days / 30);
    if (monthCount > 1 && monthCount < 12) {
      return `${monthCount} ${translationNode.Months}`;
    }
    if (monthCount === 12) {
      return showYearInMonths
        ? `12 ${translationNode.Months}`
        : `1 ${translationNode.Year}`;
    }
    if (monthCount === 1) {
      return `1 ${translationNode.Month}`;
    }
    if (monthCount === 0) {
      return `${days} ${days > 1 ? translationNode.Days : translationNode.Day}`;
    }
  }
  return null;
};

/* Remove all localstorage flags applicable in payment journey from consumption page
   as user is going ahead with payment journey from subscription page */

export const resetConsumptionPaymentJourneyFlags = () => {
  deleteLocalStorage(IS_CONSUMPTION_PAYMENT_JOURNEY);
  deleteLocalStorage(CONSUMPTION_PAYMENT_JOURNEY_PATH);
  deleteLocalStorage(CONSUMPTION_JOURNEY_CODE_REDEMPTION_OBJ);
  deleteLocalStorage(SUBSCRIPTION_ORDER_SUMMARY_BANNER);
  deleteLocalStorage(PAYMENT_OBJECT);
  deleteLocalStorage(SELECTED_PLAN);
  deleteLocalStorage(PAYMENT_JOURNEY_SOURCE);
};
export const getPlanWithCohort = (plansData: Data) => {
  if (plansData.plans.length > 0 && plansData.default_plan) {
    const cohortPlan = plansData?.plans.find(
      (plan: Plan) =>
        plan?.id === plansData?.default_plan &&
        plan.discount &&
        typeof plan.discount === 'object' &&
        Object.keys(plan.discount).length
    );
    if (cohortPlan && cohortPlan?.discount) return cohortPlan;
    else {
      return plansData.plans.find(
        (plan) =>
          plan.discount &&
          typeof plan.discount === 'object' &&
          Object.keys(plan.discount).length
      );
    }
  }
};

// Set referral paramerters id, code, name in session storage if it is present in URL
export const setReferralParametersInSession = (paramObject: ParsedUrlQuery) => {
  if (
    paramObject.id &&
    typeof paramObject.id === 'string' &&
    paramObject.code &&
    typeof paramObject.code === 'string' &&
    paramObject.name &&
    typeof paramObject.name === 'string'
  ) {
    const id = getLocalStorage(REFERRAL_ID);
    if (!id || id !== paramObject.id) {
      setLocalStorage(REFERRAL_ID, paramObject.id);
      setLocalStorage(REFERRAL_NAME, paramObject.name);
      setLocalStorage(REFERRAL_CODE, paramObject.code);
      deleteLocalStorage(REFERRAL_DISCOUNT_EXPIRY);
    }
  } else {
    const referralId = getLocalStorage(REFERRAL_ID);
    if (referralId) {
      deleteLocalStorage(REFERRAL_ID);
      deleteLocalStorage(REFERRAL_NAME);
      deleteLocalStorage(REFERRAL_CODE);
      deleteLocalStorage(REFERRAL_USERID);
    }
  }
};

// Remove fererral parameters from local storage if it is referee and discount time expired or return
export const getReferralParameters = () => {
  let name = getLocalStorage(REFERRAL_NAME);
  let id = getLocalStorage(REFERRAL_ID);
  let code = getLocalStorage(REFERRAL_CODE);
  let userId = getLocalStorage(REFERRAL_USERID);
  const discountExpiryTimestamp = getLocalStorage(REFERRAL_DISCOUNT_EXPIRY);
  if (
    name &&
    name.toString().toLowerCase() === REFERRER &&
    discountExpiryTimestamp &&
    new Date().getTime() > discountExpiryTimestamp
  ) {
    id = '';
    code = '';
    name = '';
    userId = '';
  }
  const returnObject = {
    id,
    code,
    name,
    referralUserId: userId,
    discountExpiry: discountExpiryTimestamp
  };
  return { ...returnObject };
};

export const validateReferee = () => {
  const referralData = getReferralParameters();
  const userToken = getLocalStorage(USER_TOKEN);
  const userId = parseJwt(userToken).user_id;
  const { referralUserId = '', name = '' } = referralData || {};
  if (
    name &&
    name.toString().toLowerCase() === REFERRER &&
    referralUserId === userId
  ) {
    deleteLocalStorage(REFERRAL_ID);
    deleteLocalStorage(REFERRAL_NAME);
    deleteLocalStorage(REFERRAL_CODE);
    deleteLocalStorage(REFERRAL_USERID);
    return true;
  }
  return false;
};

export const isSubscribedToContentPartner = (
  subscriptionData: subscriptionDataType,
  contentPartnerId: string
) => {
  return !!subscriptionData.currentActiveSubscriptions?.find((subscription) => {
    return !!subscription?.content_partner_details?.find((partner_details) => {
      return partner_details.content_partner_id === contentPartnerId;
    });
  });
};

export const bingeBannerEnabledCountries = [
  'US',
  'GB',
  'AU',
  'CA',
  'MY',
  'SG',
  'NZ',
  'AE',
  'SA',
  'QA',
  'OM',
  'KW',
  'BH'
];
